import { ApiError } from '../errors/api-error.js';
import { BaseApi } from './base.api.js';
import { UPLOAD_FILE_TIMEOUT_MS } from '@repo/contracts';
import { resolveOnUploadProgress, uploadAxiosConfig } from '../utils/upload-axios-config.js';

export class IncidentApi extends BaseApi {
    constructor(client) {
        super(client, '/incident');
    }

    async getDetail(id) {
        try {
            const response = await this.client.get(`${this.basePath}/${id}/detail`);
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }

    async checkCodeAvailability(params = {}) {
        const response = await this.client.get(`${this.basePath}/check-code`, { params });
        return response.data;
    }

    async getRelatedActions(id, params = {}) {
        const response = await this.client.get(`${this.basePath}/${id}/actions`, { params });
        return response.data;
    }

    async getLinkableActions(id, params = {}) {
        const response = await this.client.get(`${this.basePath}/${id}/actions/linkable`, { params });
        return response.data;
    }

    async linkActions(id, actionIds, extra = {}) {
        const response = await this.client.post(`${this.basePath}/${id}/actions`, { actionIds, ...extra });
        return response.data;
    }

    async unlinkActions(id, actionIds) {
        const response = await this.client.post(`${this.basePath}/${id}/actions/unlink`, { actionIds });
        return response.data;
    }

    async unlinkAction(id, actionId) {
        const response = await this.client.delete(`${this.basePath}/${id}/actions/${actionId}`);
        return response.data;
    }

    async reorderActions(id, { actionIds } = {}) {
        const response = await this.client.put(`${this.basePath}/${id}/actions/reorder`, { actionIds });
        return response.data;
    }

    async getRelatedTickets(id, params = {}) {
        const response = await this.client.get(`${this.basePath}/${id}/tickets`, { params });
        return response.data;
    }

    async getLinkableTickets(id, params = {}) {
        const response = await this.client.get(`${this.basePath}/${id}/tickets/linkable`, { params });
        return response.data;
    }

    async linkTickets(id, ticketIds) {
        const response = await this.client.post(`${this.basePath}/${id}/tickets`, { ticketIds });
        return response.data;
    }

    async unlinkTickets(id, ticketIds) {
        const response = await this.client.post(`${this.basePath}/${id}/tickets/unlink`, { ticketIds });
        return response.data;
    }

    async unlinkTicket(id, ticketId) {
        const response = await this.client.delete(`${this.basePath}/${id}/tickets/${ticketId}`);
        return response.data;
    }

    async getRelatedProblems(id, params = {}) {
        const response = await this.client.get(`${this.basePath}/${id}/problems`, { params });
        return response.data;
    }

    async getLinkableProblems(id, params = {}) {
        const response = await this.client.get(`${this.basePath}/${id}/problems/linkable`, { params });
        return response.data;
    }

    async linkProblems(id, problemIds) {
        const response = await this.client.post(`${this.basePath}/${id}/problems`, { problemIds });
        return response.data;
    }

    async unlinkProblems(id, problemIds) {
        const response = await this.client.post(`${this.basePath}/${id}/problems/unlink`, { problemIds });
        return response.data;
    }

    async unlinkProblem(id, problemId) {
        const response = await this.client.delete(`${this.basePath}/${id}/problems/${problemId}`);
        return response.data;
    }

    async getRelatedChangeRequests(id, params = {}) {
        const response = await this.client.get(`${this.basePath}/${id}/change-requests`, { params });
        return response.data;
    }

    async getLinkableChangeRequests(id, params = {}) {
        const response = await this.client.get(`${this.basePath}/${id}/change-requests/linkable`, { params });
        return response.data;
    }

    async linkChangeRequests(id, changeRequestIds) {
        const response = await this.client.post(`${this.basePath}/${id}/change-requests`, { changeRequestIds });
        return response.data;
    }

    async unlinkChangeRequests(id, changeRequestIds) {
        const response = await this.client.post(`${this.basePath}/${id}/change-requests/unlink`, { changeRequestIds });
        return response.data;
    }

    async unlinkChangeRequest(id, changeRequestId) {
        const response = await this.client.delete(`${this.basePath}/${id}/change-requests/${changeRequestId}`);
        return response.data;
    }

    async getPartyTemplate(params = {}) {
        const response = await this.client.get(`${this.basePath}/party-template`, { params });
        return response.data;
    }

    async getParty(id) {
        const response = await this.client.get(`${this.basePath}/${id}/party`);
        return response.data;
    }

    async syncParty(id, data) {
        const response = await this.client.put(`${this.basePath}/${id}/party`, data);
        return response.data;
    }

    async getComments(incidentId, params = {}) {
        const response = await this.client.get('/incident-comments', {
            params: { limit: 300, ...params, incident: incidentId },
        });
        return response.data;
    }

    async addComment(data) {
        const response = await this.client.post('/incident-comments', data);
        return response.data;
    }

    async updateComment(commentId, data) {
        const response = await this.client.put(`/incident-comments/${commentId}`, data);
        return response.data;
    }

    async deleteComment(commentId) {
        const response = await this.client.delete(`/incident-comments/${commentId}`);
        return response.data;
    }

    async getAttachments(id) {
        const response = await this.client.get(`${this.basePath}/${id}/attachments`);
        return response.data;
    }

    async uploadAttachment(id, file, category = 'attachment', optionsOrCallback) {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('category', category);
        const response = await this.client.post(
            `${this.basePath}/${id}/attachments/upload`,
            formData,
            uploadAxiosConfig({
                timeout: UPLOAD_FILE_TIMEOUT_MS,
                onUploadProgress: resolveOnUploadProgress(optionsOrCallback),
                headers: { 'Content-Type': 'multipart/form-data' },
            }),
        );
        return response.data;
    }

    async deleteAttachment(id, attachmentId) {
        const response = await this.client.delete(`${this.basePath}/${id}/attachments/${attachmentId}`);
        return response.data;
    }

    async submit(id) {
        try {
            const response = await this.client.post(`${this.basePath}/${id}/submit`);
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }

    async assign(id, data) {
        try {
            const response = await this.client.post(`${this.basePath}/${id}/assign`, data);
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }

    async claim(id) {
        try {
            const response = await this.client.post(`${this.basePath}/${id}/claim`);
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }

    async acceptAssignment(id) {
        try {
            const response = await this.client.post(`${this.basePath}/${id}/accept-assignment`);
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }

    async rejectAssignment(id, data) {
        try {
            const response = await this.client.post(`${this.basePath}/${id}/reject-assignment`, data);
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }

    async resolve(id, data = {}) {
        try {
            const response = await this.client.post(`${this.basePath}/${id}/resolve`, data);
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }

    async close(id, data = {}) {
        try {
            const response = await this.client.post(`${this.basePath}/${id}/close`, data);
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }

    async getWorkflowTemplateOptions(params = {}) {
        try {
            const response = await this.client.get(`${this.basePath}/workflow-template-options`, { params });
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }

    async submitPendingUpdate(id) {
        try {
            const response = await this.client.post(`${this.basePath}/${id}/submit-pending-update`, {});
            return response.data;
        } catch (error) {
            throw ApiError.fromResponse(error);
        }
    }
}
